import { Slot, Slottable } from '@radix-ui/react-slot'
import * as React from 'react'

import { cn } from '../lib/cn'
import { EntryTitleContext } from './entry-title-context'

/* Family 0 · Typography — the only place that names the theme's type scale. Headings
   take `font-display`: in this brand a heading is not the body font made bigger.
   Why the utilities are used whole, and why each takes `asChild`: CODING_STANDARDS.md. */
interface TypographyProps extends React.HTMLAttributes<HTMLElement> {
  /** Renders the child instead of the default element, handing it the classes: the
   *  heading level follows the document, not the appearance. */
  asChild?: boolean
}

/* Aliases and not empty interfaces, with distinct names because they are the public
 * surface: an app types its props on `TextProps`, never on an internal one. */
export interface PageTitleProps extends TypographyProps {
  /** The page whose subject is a named thing — a club, a squad — where the name carries
   *  the page instead of labelling it: the hero scale, in the brand's lime. */
  subject?: boolean
}

/** The main heading of a page: an `h1`, one per page. */
export const PageTitle = React.forwardRef<HTMLHeadingElement, PageTitleProps>(
  ({ className, asChild = false, subject = false, ...props }, ref) => {
    // Shown in an entrance sheet, the page's title is the sheet's: the sheet names itself by it.
    const SheetTitle = React.useContext(EntryTitleContext)
    if (SheetTitle !== null)
      return <SheetTitle ref={ref} asChild={asChild} className={className} {...props} />
    const Component = asChild ? Slot : 'h1'
    return (
      <Component
        ref={ref}
        className={cn(
          'font-display uppercase',
          subject ? 'text-hero text-primary-text' : 'text-page-title',
          className,
        )}
        {...props}
      />
    )
  },
)
PageTitle.displayName = 'PageTitle'

export interface SectionTitleProps extends TypographyProps {
  /** How many things the section holds, after the words and in the brand's lime. The
   *  caller decides when there is a number to state: a count shown while its query is in
   *  flight, or a `0` beside «da preparare», would announce something untrue. */
  count?: number
  /** A chip or a link at the far end of the title's row. It sits beside the heading and not
   *  inside it, so it never becomes part of the heading's name. */
  aside?: React.ReactNode
}

/** The section-title type, shared with `CardTitle` (which drops the slash) so they cannot drift. */
export const ENTRY_HEADLINE_TYPE =
  'font-display text-entry-headline uppercase entry:text-entry-headline-wide'

export const SECTION_TITLE_TYPE = 'font-display text-section-title uppercase'

/* `bg-primary`, not a fixed lime: the mark follows the surface — lime on the page and in an
   ink island, black inside a lime card, where `--primary` is the ink. */
function Slash() {
  return (
    <span
      data-slash
      aria-hidden="true"
      className="h-[0.8em] w-2 shrink-0 -skew-x-[18deg] bg-primary"
    />
  )
}

/** The heading of a section inside a page: an `h2` by default, led by a slanted slash. */
export const SectionTitle = React.forwardRef<HTMLHeadingElement, SectionTitleProps>(
  ({ className, asChild = false, count, aside, children, ...props }, ref) => {
    const Component = asChild ? Slot : 'h2'
    const heading = (
      <Component
        ref={ref}
        className={cn('flex items-center gap-3', SECTION_TITLE_TYPE, className)}
        {...props}
      >
        <Slash />
        {/* The words are one flex item, however many nodes they hold. */}
        <Slottable>{asChild ? children : <span>{children}</span>}</Slottable>
        {/* The separating space is a text node, not the gap: the gap is a layout property
            and leaves the heading reading «Convocazioni3» to anything that concatenates text. */}
        {count === undefined ? null : (
          <>
            {' '}
            <span className="text-primary-text">{count}</span>
          </>
        )}
      </Component>
    )
    if (aside == null) return heading
    return (
      <div className="flex items-center justify-between gap-3">
        {heading}
        <div className="shrink-0">{aside}</div>
      </div>
    )
  },
)
SectionTitle.displayName = 'SectionTitle'

export type TextProps = TypographyProps

/** Reading text. */
export const Text = React.forwardRef<HTMLParagraphElement, TextProps>(
  ({ className, asChild = false, ...props }, ref) => {
    const Component = asChild ? Slot : 'p'
    return <Component ref={ref} className={cn('text-body', className)} {...props} />
  },
)
Text.displayName = 'Text'

export type MutedTextProps = TypographyProps

/** Supporting text: descriptions, counts, marginal notes. Scale and tone together are one
 *  decision — splitting them would put the choice of grey back into the pages. */
export const MutedText = React.forwardRef<HTMLParagraphElement, MutedTextProps>(
  ({ className, asChild = false, ...props }, ref) => {
    const Component = asChild ? Slot : 'p'
    return (
      <Component
        ref={ref}
        className={cn('text-note text-muted-foreground', className)}
        {...props}
      />
    )
  },
)
MutedText.displayName = 'MutedText'

export type CodeTextProps = TypographyProps

/** A machine-readable string shown verbatim: a token, an id, a URL to be copied.
 *  `break-all` belongs to the decision — what it shows has no spaces to break at. */
export const CodeText = React.forwardRef<HTMLElement, CodeTextProps>(
  ({ className, asChild = false, ...props }, ref) => {
    const Component = asChild ? Slot : 'code'
    return (
      <Component
        ref={ref}
        className={cn('font-mono text-note break-all text-foreground', className)}
        {...props}
      />
    )
  },
)
CodeText.displayName = 'CodeText'

export interface EyebrowProps extends TypographyProps {
  /** A control at the far end of the kicker's row, as `SectionTitle`'s: beside the words, never
   *  part of them. */
  aside?: React.ReactNode
  /** The row an `aside` gets, without one: a kicker beside another that has a control, as in the two
   *  columns of the theater, stands at its height. */
  row?: boolean
}

/** The lime kicker above a title: the smallest piece of the brand that is unmistakably it.
 *  Not a heading — it labels the section the heading names, so it defaults to a `p` and the
 *  document outline stays with `SectionTitle`. */
export const Eyebrow = React.forwardRef<HTMLParagraphElement, EyebrowProps>(
  ({ className, asChild = false, aside, row = false, ...props }, ref) => {
    const Component = asChild ? Slot : 'p'
    const kicker = (
      <Component
        ref={ref}
        className={cn('font-display text-eyebrow text-primary-text uppercase', className)}
        {...props}
      />
    )
    if (aside == null && !row) return kicker
    return (
      <div className="flex min-h-(--size-control-sm) items-center justify-between gap-3">
        {kicker}
        {aside == null ? null : <div className="shrink-0">{aside}</div>}
      </div>
    )
  },
)
Eyebrow.displayName = 'Eyebrow'
